import React from 'react';
import {
    Box,
    Collapse,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getPortalPathForView } from '../../navigation/portalRoutes';

export const PORTAL_DRAWER_WIDTH = 240;

function NavigationList({
    effectiveIsAdmin,
    expandedGroups,
    isMenuItemVisible,
    isProcessing,
    isVisualRefreshPreview,
    locationSearch,
    menuGroups,
    onNavigate,
    onToggleGroup,
    processingMenuItems,
    renderMenuItemLabel,
    selectedView,
    useLinks
}) {
    const linkProps = (url) => (
        useLinks
            ? {
                component: Link,
                to: isVisualRefreshPreview ? `${url}${locationSearch}` : url
            }
            : {}
    );

    if (isProcessing) {
        return processingMenuItems.map((item) => (
            <ListItem key={item.id} disablePadding>
                <ListItemButton
                    {...linkProps(item.url)}
                    selected={selectedView === item.id}
                    onClick={() => onNavigate(item.id)}
                >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                </ListItemButton>
            </ListItem>
        ));
    }

    return Object.entries(menuGroups).map(([groupKey, group]) => {
        const filteredItems = group.items.filter(isMenuItemVisible);

        if (group.adminOnly && !effectiveIsAdmin) return null;
        if (filteredItems.length === 0) return null;

        if (groupKey === 'dashboard') {
            const dashboardUrl = getPortalPathForView('dashboard');
            return (
                <ListItem key={groupKey} disablePadding>
                    <ListItemButton
                        component={Link}
                        to={isVisualRefreshPreview ? `${dashboardUrl}${locationSearch}` : dashboardUrl}
                        selected={selectedView === 'dashboard'}
                        onClick={() => onNavigate('dashboard')}
                    >
                        <ListItemIcon>{group.icon}</ListItemIcon>
                        <ListItemText primary={group.label} />
                    </ListItemButton>
                </ListItem>
            );
        }

        return (
            <React.Fragment key={groupKey}>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => onToggleGroup(groupKey)}>
                        <ListItemIcon>{group.icon}</ListItemIcon>
                        <ListItemText primary={group.label} />
                        {expandedGroups[groupKey] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>
                <Collapse in={expandedGroups[groupKey]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {filteredItems.map((item) => {
                            const url = getPortalPathForView(item.id);
                            return (
                                <ListItem key={item.id} disablePadding sx={{ pl: 4 }}>
                                    <ListItemButton
                                        {...linkProps(url)}
                                        selected={selectedView === item.id}
                                        onClick={() => onNavigate(item.id)}
                                    >
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText primary={renderMenuItemLabel(item)} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
                <Divider />
            </React.Fragment>
        );
    });
}

function PortalSidebar({
    mobileOpen,
    onCloseMobile,
    onNavigate,
    ...navigationProps
}) {
    const handleMobileNavigate = (viewId) => {
        onNavigate(viewId);
        onCloseMobile();
    };

    return (
        <Box component="nav" sx={{ width: { sm: PORTAL_DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onCloseMobile}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: PORTAL_DRAWER_WIDTH }
                }}
            >
                <Toolbar />
                <Box sx={{ overflow: 'auto' }}>
                    <List>
                        <NavigationList
                            {...navigationProps}
                            onNavigate={handleMobileNavigate}
                            useLinks={false}
                        />
                    </List>
                </Box>
            </Drawer>

            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: PORTAL_DRAWER_WIDTH }
                }}
            >
                <Toolbar />
                <Box sx={{ overflow: 'auto' }}>
                    <List>
                        <NavigationList
                            {...navigationProps}
                            onNavigate={onNavigate}
                            useLinks
                        />
                    </List>
                </Box>
            </Drawer>
        </Box>
    );
}

export default PortalSidebar;
